/** App-wide MUI DataGrid look — use on every grid. */

const FONT =
    '"Nunito", "Avenir Next", "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif';

/** Spread onto every <DataGrid /> alongside sx. */
export const appDataGridDefaultProps = {
    columnHeaderHeight: 44,
    rowHeight: 48,
    disableColumnFilter: true,
    disableColumnMenu: true,
    // MUI default is ['asc','desc',null]. With controlled sort + a desc default,
    // the next click clears to null and our fallback re-applies desc — looks locked.
    sortingOrder: ["asc", "desc"],
};

export const appDataGridSx = {
    border: "none",
    height: "100%",
    fontFamily: FONT,
    "--DataGrid-rowBorderColor": "#e2e8f0",
    "& .MuiDataGrid-main": {
        borderRadius: 0,
    },
    /* Let DataGrid's own row--borderBottom draw the header divider (avoid double border / gap). */
    "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#ffffff",
        borderBottom: "none",
    },
    "& .MuiDataGrid-columnHeader": {
        outline: "none !important",
        paddingLeft: "10px",
        paddingRight: "10px",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "#5c6570",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
        gap: 0,
    },
    "& .MuiDataGrid-columnSeparator": {
        color: "#e2e8f0",
    },
    /* MUI hides unsorted sort icons (opacity 0 / width 0). Keep a quiet affordance
       on every sortable header so server-sorted grids look clickable. */
    "& .MuiDataGrid-columnHeader--sortable .MuiDataGrid-iconButtonContainer": {
        visibility: "visible !important",
        width: "auto !important",
    },
    "& .MuiDataGrid-columnHeader--sortable:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortButton": {
        opacity: "0.4 !important",
    },
    "& .MuiDataGrid-columnHeader--sortable:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortButton > *": {
        opacity: "1 !important",
    },
    "& .MuiDataGrid-columnHeader--sortable:hover .MuiDataGrid-sortButton, & .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortButton": {
        opacity: "1 !important",
    },
    "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
        borderBottom: "none",
    },
    "& .MuiDataGrid-row": {
        cursor: "default",
        transition: "background-color 120ms ease",
        "&:hover": {
            backgroundColor: "rgba(13, 148, 136, 0.04)",
        },
        "&.Mui-selected": {
            backgroundColor: "rgba(13, 148, 136, 0.08)",
            "&:hover": {
                backgroundColor: "rgba(13, 148, 136, 0.12)",
            },
        },
    },
    "& .MuiDataGrid-cell": {
        borderColor: "#eef2f6",
        fontSize: 13.5,
        color: "#1c2430",
        py: 0,
        display: "flex",
        alignItems: "center",
        px: "10px",
    },
    "& .MuiDataGrid-cellContent": {
        fontSize: 13.5,
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
        outline: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
        backgroundColor: "#ffffff",
    },
    "& .MuiCheckbox-root": {
        color: "#cbd5e1",
        "&.Mui-checked": {
            color: "#0d9488",
        },
    },

    /* Footer / pagination */
    "& .MuiDataGrid-footerContainer": {
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        minHeight: 52,
        px: 1.5,
    },
    "& .MuiTablePagination-root": {
        width: "100%",
        overflow: "visible",
        color: "#5c6570",
        fontFamily: FONT,
    },
    "& .MuiTablePagination-toolbar": {
        minHeight: 52,
        paddingLeft: 4,
        paddingRight: 4,
        gap: 4,
    },
    "& .MuiTablePagination-spacer": {
        flex: "1 1 auto",
    },
    "& .MuiTablePagination-selectLabel": {
        margin: 0,
        fontSize: 13,
        fontWeight: 500,
        color: "#5c6570",
    },
    "& .MuiTablePagination-displayedRows": {
        margin: 0,
        fontSize: 13,
        fontWeight: 600,
        color: "#1c2430",
        letterSpacing: "-0.01em",
    },
    "& .MuiTablePagination-input": {
        marginRight: 16,
        marginLeft: 4,
    },
    /* Quiet page-size control — no boxed outline */
    "& .MuiTablePagination-select": {
        fontSize: 13,
        fontWeight: 600,
        fontFamily: FONT,
        color: "#1c2430",
        borderRadius: 0,
        border: "none",
        backgroundColor: "transparent",
        paddingTop: "2px",
        paddingBottom: "2px",
        paddingLeft: "4px",
        paddingRight: "20px",
        marginRight: 0,
        marginLeft: 0,
        "&:focus": {
            borderRadius: 0,
            backgroundColor: "transparent",
        },
    },
    "& .MuiTablePagination-selectIcon": {
        color: "#9aa3ad",
        right: 0,
    },
    "& .MuiTablePagination-actions": {
        marginLeft: 4,
        display: "flex",
        gap: 6,
        "& .MuiIconButton-root": {
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            width: 32,
            height: 32,
            padding: 0,
            color: "#1c2430",
            "& .MuiSvgIcon-root": {
                fontSize: 18,
                margin: 0,
            },
            "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#cbd5e1",
            },
            "&.Mui-disabled": {
                borderColor: "#eef2f6",
                color: "#cbd5e1",
                backgroundColor: "#ffffff",
            },
        },
    },
};

/** Visible outlined search field used across grids. */
export const appSearchFieldSx = {
    width: { xs: "100%", sm: 260 },
    "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        fontSize: 14,
        "& fieldset": {
            borderColor: "#94a3b8",
            borderWidth: 1,
        },
        "&:hover fieldset": {
            borderColor: "#64748b",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0d9488",
            borderWidth: 1.5,
        },
    },
    "& .MuiOutlinedInput-input": {
        py: "8.5px",
    },
};

/** Empty selection model for DataGrid v9+ */
export const emptySelectionModel = () => ({
    type: "include",
    ids: new Set(),
});

/**
 * Normalize rowSelectionModel (v9 object or legacy array) to an id list.
 * Select-all uses `{ type: "exclude", ids }` — pass current `rows` to resolve that.
 */
export function selectionIds(model, rows) {
    if (!model) return [];
    if (Array.isArray(model)) return model;
    const rawIds =
        model.ids instanceof Set
            ? [...model.ids]
            : Array.isArray(model.ids)
              ? model.ids
              : [];
    if (model.type === "exclude") {
        const rowIds = (rows || []).map((row) =>
            row && typeof row === "object" && "id" in row ? row.id : row
        );
        if (!rowIds.length) return [];
        if (!rawIds.length) return rowIds;
        const excluded = new Set(rawIds.map(String));
        return rowIds.filter((id) => !excluded.has(String(id)));
    }
    return rawIds;
}

/**
 * Optional extras layered onto the shared grid styles.
 * Later keys win for top-level; nested selectors are shallow-merged.
 */
export function mergeDataGridSx(...extras) {
    return extras.reduce((acc, extra) => {
        if (!extra) return acc;
        return { ...acc, ...extra };
    }, { ...appDataGridSx });
}

export const appDataGridSlotProps = {
    pagination: {
        labelRowsPerPage: "Rows per page",
        showFirstButton: false,
        showLastButton: false,
    },
};
